import {
  VOICE_PATH,
  VoiceServerEvent,
  type VoiceEndReason,
  type VoiceErrorCode,
  type VoicePhrase,
} from '@myrewards/contracts';
import { FEEDBACK_VOICE_LIMITS as V } from '@myrewards/domain';

// APP-104 §3.2 and §8, Task 16's protocol: one recording, from the click to the words.
//
// - The microphone starts at the click and its audio is held here from then: the session can't
//   start before the draft is open (the open follows the page's picture, up to 12 s), so the
//   first words would otherwise be lost.
// - The socket opens once the microphone is heard and the draft's ticket is in hand (the server
//   wants `start` within 10 s of the upgrade). Nothing but `start` goes before `started`.
// - A start that fails (no `started` within 20 s, the socket closed, the ticket fetch failed, a
//   busy server past the ticket's life) is tried once more with a new ticket, keeping what's held.
//   Only after that, or 75 s after the microphone was first heard, is voice given up.
// - Then the held audio goes in order: a 1 s burst, then no faster than 1.5× real time. The
//   server's back-pressure bound counts from `started`, so a faster dump would end as `backlog`.
// - Done stops the microphone and still sends what's held, then `stop` (a `stop` before the held
//   audio would end the session before those words were heard).
// - A backlog over 20 s once started means the connection is slow: recording finishes.
//
// Audio lives only in the `held` queue until the socket takes it. Nothing is stored, logged or
// kept after the recording ends; the words leave only through `events`.

/** After `stop`, the saved words have this long to arrive (§3.2: up to 20 s). */
export const CONFIRM_MS = 20_000;
/** Each try at starting the session: `started` must come within this long of connecting. */
export const CONNECT_MS = 20_000;
/** The whole start, however many tries: this long after the microphone was first heard. */
export const START_BOUND_MS = 75_000;
/** The microphone itself (the question, the worklet, the audio context) must open within this. */
export const MIC_START_MS = 10_000;
/** A start that failed is tried this many more times, with a new ticket. */
const START_RETRIES = 1;
/** `busy` (the server is full): try again with the same ticket after this long. */
export const BUSY_RETRY_MS = 2_000;
/** A ticket works for 120 s; a busy retry stops before it could have run out. */
const TICKET_FRESH_MS = V.ticketMs - 15_000;
/** Unsent audio over this long, once started, finishes the recording (§8). */
export const SLOW_BACKLOG_MS = 20_000;

const BYTES_PER_MS = V.bytesPerSecond / 1_000;
/** The burst at `started`: 1 s of audio. */
const BURST_BYTES = V.bytesPerSecond;
/** Then 1.5× real time. */
const PACE_BYTES_PER_MS = 1.5 * BYTES_PER_MS;
/** Bytes the socket may hold unsent before we wait (2 s of audio): a slow link backs up here. */
const SOCKET_HIGH_WATER = 2 * V.bytesPerSecond;
const PUMP_MS = 50;
const OPEN = 1;

export interface Microphone {
  /**
   * Stops capturing; resolves once the last partial frame has been handed over and the tracks are
   * stopped (never waiting on the audio context's close).
   */
  stop(): Promise<void>;
}
/**
 * Starts the microphone; each 100 ms of 16 kHz PCM16 arrives through `onFrame`. When `signal`
 * aborts before it has opened, whatever it already holds (the tracks, the audio context) is let go.
 */
export type OpenMicrophone = (
  onFrame: (pcm: ArrayBuffer) => void,
  signal?: AbortSignal,
) => Promise<Microphone>;

/** What `start` carries: a feedback draft's ticket, or a Help chat question's (APP-124). */
export type VoiceTicket = { reportId: string; ticket: string } | { scope: 'help'; ticket: string };

/** The parts of a WebSocket the recorder uses. */
export interface VoiceSocket {
  readonly readyState: number;
  readonly bufferedAmount: number;
  binaryType: BinaryType;
  send(data: string | ArrayBuffer): void;
  close(code?: number): void;
  onopen: ((event: Event) => unknown) | null;
  onmessage: ((event: MessageEvent) => unknown) | null;
  onclose: ((event: CloseEvent) => unknown) | null;
  onerror: ((event: Event) => unknown) | null;
}

export interface Timers {
  now(): number;
  set(fn: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

const REAL_TIMERS: Timers = {
  // Monotonic: a clock change mid-recording never bends the pace.
  now: () => performance.now(),
  set: (fn, ms) => window.setTimeout(fn, ms),
  clear: (handle) => {
    window.clearTimeout(handle as number);
  },
};

/**
 * The microphone didn't open within `MIC_START_MS`. MicHelp explains it as a closed question when
 * the browser is still asking, otherwise as a busy microphone.
 */
export class MicrophoneStartTimeout extends Error {
  constructor() {
    super('The microphone did not start in time');
    this.name = 'MicrophoneStartTimeout';
  }
}

/** How a recording ended. */
export type RecorderEnd =
  /** Ended in order: these are the saved words. */
  | { kind: 'saved'; reason: VoiceEndReason }
  /** Type instead: the words heard so far; the server still saves them. */
  | { kind: 'typed' }
  /** Some words may be missing (an error, a lost socket, no `saved` in time). */
  | { kind: 'unconfirmed' }
  /** The backlog passed 20 s: recording stopped early. */
  | { kind: 'slow' }
  /** The session never started, so nothing was transcribed. */
  | { kind: 'not_started'; code: VoiceErrorCode | 'timeout' | 'no_connection' }
  /** The microphone wouldn't open (explained by micAccess). */
  | { kind: 'microphone'; error: unknown };

export interface RecorderResult {
  end: RecorderEnd;
  /** The final phrases, in order. */
  phrases: VoicePhrase[];
  /** The phrase being spoken when it ended (shown, never confirmed). */
  partial: string;
}

export type RecorderPhase = 'starting' | 'recording' | 'finishing';

export interface RecorderEvents {
  phase(phase: RecorderPhase): void;
  /** The loudness of the last frame, 0 to 1 (for the level bars). */
  level(rms: number): void;
  partial(text: string): void;
  phrase(phrase: VoicePhrase): void;
  /** The session's audio cap, once it has started. */
  limit(maxMs: number): void;
  /** Once, at the end (never after `cancel`). */
  end(result: RecorderResult): void;
}

export interface RecorderOptions {
  openMicrophone: OpenMicrophone;
  /**
   * The ticket for `start`: the draft's (`renew` opens the draft again for a new one), or a Help
   * chat one. Null: no voice now.
   */
  ticket: (renew: boolean) => Promise<VoiceTicket | null>;
  connect: () => VoiceSocket;
  /** The most audio this recording may take before the session says (≤ 5 minutes). */
  capMs?: number;
  timers?: Timers;
  events: RecorderEvents;
}

export interface Recorder {
  /** Done: send what's held, then wait for the saved words. */
  done(): void;
  /** Type instead: end now with the words heard; the server still saves them. */
  typeInstead(): void;
  /** Cancel feedback: end everything, report nothing. */
  cancel(): void;
}

/** The voice socket, on our own origin (`connect-src 'self'`). */
export function voiceUrl(): string {
  return `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}${VOICE_PATH}`;
}

/** The words a recording gave, as one paragraph: its phrases, then what was being said. */
export function wordsOf(result: RecorderResult): string {
  return [...result.phrases.map((p) => p.text.trim()), result.partial.trim()]
    .filter((w) => w !== '')
    .join(' ');
}

function rmsOf(pcm: ArrayBuffer): number {
  const samples = new Int16Array(pcm, 0, Math.floor(pcm.byteLength / 2));
  if (samples.length === 0) return 0;
  let sum = 0;
  for (const v of samples) sum += v * v;
  return Math.sqrt(sum / samples.length) / 32_768;
}

function parse(data: unknown): VoiceServerEvent | null {
  if (typeof data !== 'string') return null;
  try {
    const parsed = VoiceServerEvent.safeParse(JSON.parse(data));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function startRecorder(opts: RecorderOptions): Recorder {
  const t = opts.timers ?? REAL_TIMERS;
  const events = opts.events;

  let phase: RecorderPhase | 'ended' = 'starting';
  let mic: Microphone | null = null;
  let micStopping: Promise<void> | null = null;
  /** The microphone has handed over its last frame. */
  let micDone = false;
  let socket: VoiceSocket | null = null;
  let ticket: VoiceTicket | null = null;
  let ticketAt = 0;
  let started = false;
  let startedAt = 0;
  let sentBytes = 0;
  /** What was held when the session started (a long start is not a slow link). */
  let heldAtStart = 0;
  const held: ArrayBuffer[] = [];
  let heldBytes = 0;
  let capturedBytes = 0;
  let capBytes = Math.min(opts.capMs ?? V.recordingMs, V.recordingMs) * BYTES_PER_MS;
  let stopSent = false;
  /** Why it is finishing: Done (or the cap), or a slow link. */
  let finishingSlow = false;
  let renewals = 0;
  let busyTries = 0;
  /** Starts that failed (timeout, lost connection, ticket fetch, busy too long). */
  let startFailures = 0;
  const phrases: VoicePhrase[] = [];
  let partial = '';
  const timers = new Set<unknown>();

  const later = (fn: () => void, ms: number): unknown => {
    const handle = t.set(() => {
      timers.delete(handle);
      fn();
    }, ms);
    timers.add(handle);
    return handle;
  };
  const cancelTimer = (handle: unknown): void => {
    if (handle === null) return;
    t.clear(handle);
    timers.delete(handle);
  };
  let connectTimer: unknown = null;
  let boundTimer: unknown = null;
  let pumpTimer: unknown = null;
  let drainTimer: unknown = null;
  /** After Type instead, the socket waits this long at most for the server to close it. */
  let lingerTimer: unknown = null;

  const setPhase = (next: RecorderPhase): void => {
    phase = next;
    events.phase(next);
  };
  const ended = (): boolean => phase === 'ended';

  function stopMic(): Promise<void> {
    micStopping ??= (async () => {
      const m = mic;
      if (m !== null)
        try {
          await m.stop();
        } catch {
          // Already gone.
        }
      micDone = true;
    })();
    return micStopping;
  }

  function closeSocket(code = 1000): void {
    const ws = socket;
    socket = null;
    if (ws === null) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    try {
      ws.close(code);
    } catch {
      // Already closed.
    }
  }

  function finishWith(end: RecorderEnd, keepSocket = false): void {
    if (ended()) return;
    phase = 'ended';
    for (const handle of timers) t.clear(handle);
    timers.clear();
    void stopMic();
    held.length = 0;
    heldBytes = 0;
    if (!keepSocket) closeSocket();
    events.end({ end, phrases: [...phrases], partial });
  }

  function notStarted(code: VoiceErrorCode | 'timeout' | 'no_connection'): void {
    finishWith({ kind: 'not_started', code });
  }

  function sendStop(): void {
    if (stopSent || socket === null) return;
    stopSent = true;
    socket.send(JSON.stringify({ type: 'stop' }));
    cancelTimer(drainTimer);
    later(() => {
      finishWith({ kind: 'unconfirmed' });
    }, CONFIRM_MS);
  }

  /** Sends held audio as the pace allows; once all is sent after Done, `stop`. */
  function pump(): void {
    cancelTimer(pumpTimer);
    pumpTimer = null;
    const ws = socket;
    if (ended() || !started || stopSent || ws === null || ws.readyState !== OPEN) return;
    const allowed = BURST_BYTES + PACE_BYTES_PER_MS * (t.now() - startedAt);
    for (let next = held[0]; next !== undefined; next = held[0]) {
      if (sentBytes + next.byteLength > allowed || ws.bufferedAmount > SOCKET_HIGH_WATER) break;
      held.shift();
      heldBytes -= next.byteLength;
      sentBytes += next.byteLength;
      ws.send(next);
    }
    if (held.length > 0) pumpTimer = later(pump, PUMP_MS);
    else if (phase === 'finishing' && micDone) sendStop();
  }

  /**
   * Finishing waits for the held audio to drain at the pace; if it takes much longer than the
   * pace allows, the link is slow: the rest is dropped and `stop` goes now.
   */
  function watchDrain(): void {
    if (drainTimer !== null || stopSent) return;
    const heldMs = heldBytes / BYTES_PER_MS;
    drainTimer = later(
      () => {
        drainTimer = null;
        if (stopSent || ended()) return;
        finishingSlow = true;
        held.length = 0;
        heldBytes = 0;
        sendStop();
      },
      Math.max(0, heldMs - 1_000) / 1.5 + 5_000,
    );
  }

  /** Done, the cap, or a slow link: no more audio; send what's held, then `stop`. */
  function finish(slow: boolean): void {
    if (phase !== 'recording') return;
    finishingSlow = slow;
    setPhase('finishing');
    if (slow) {
      // The link can't keep up: what's not sent now never will be in time.
      held.length = 0;
      heldBytes = 0;
    }
    void stopMic().then(() => {
      if (ended()) return;
      if (slow) {
        held.length = 0;
        heldBytes = 0;
      }
      if (started) {
        watchDrain();
        pump();
      }
      // Not started yet: `started` sends what's held; the connect deadline still applies.
    });
  }

  function onFrame(pcm: ArrayBuffer): void {
    if (ended() || stopSent || micDone || pcm.byteLength < 2) return;
    events.level(rmsOf(pcm));
    held.push(pcm);
    heldBytes += pcm.byteLength;
    capturedBytes += pcm.byteLength;
    if (phase === 'recording' && capturedBytes >= capBytes) {
      finish(false);
      return;
    }
    if (!started) return;
    if (
      phase === 'recording' &&
      heldBytes > Math.max(SLOW_BACKLOG_MS * BYTES_PER_MS, heldAtStart)
    ) {
      finish(true);
      return;
    }
    pump();
  }

  function onEvent(event: VoiceServerEvent): void {
    // After Type instead the socket stays only for the server to save and close.
    if (ended()) {
      if (event.type === 'closed') {
        if (lingerTimer !== null) t.clear(lingerTimer);
        lingerTimer = null;
        closeSocket();
      }
      return;
    }
    switch (event.type) {
      case 'started': {
        started = true;
        startedAt = t.now();
        cancelTimer(connectTimer);
        cancelTimer(boundTimer);
        heldAtStart = heldBytes;
        // The browser's own cap sits just past the session's, so the server's reason wins.
        capBytes = Math.min(capBytes, (event.maxMs + 500) * BYTES_PER_MS);
        events.limit(event.maxMs);
        if (phase === 'finishing' && micDone) watchDrain();
        pump();
        return;
      }
      case 'partial':
        partial = event.text;
        events.partial(event.text);
        return;
      case 'phrase': {
        const p = { text: event.text, offsetMs: event.offsetMs };
        phrases.push(p);
        partial = '';
        events.phrase(p);
        return;
      }
      case 'saved':
        phrases.splice(0, phrases.length, ...event.transcript);
        partial = '';
        finishWith(finishingSlow ? { kind: 'slow' } : { kind: 'saved', reason: event.reason });
        return;
      case 'error':
        onError(event.code);
        return;
      case 'closed':
        lost();
        return;
    }
  }

  /**
   * A start that failed for a reason a new try can fix: once more with a new ticket, keeping the
   * held audio and the "Recording" bar; after that, voice is given up.
   */
  function startFailed(code: 'timeout' | 'no_connection' | 'busy'): void {
    if (ended() || started) return;
    cancelTimer(connectTimer);
    closeSocket();
    if (startFailures < START_RETRIES) {
      startFailures += 1;
      void begin(true);
      return;
    }
    notStarted(code);
  }

  function onError(code: VoiceErrorCode): void {
    if (!started) {
      if ((code === 'ticket_used' || code === 'ticket_expired') && renewals < 2) {
        renewals += 1;
        cancelTimer(connectTimer);
        closeSocket();
        void begin(true);
        return;
      }
      if (code === 'busy') {
        if (busyTries < 5 && t.now() - ticketAt < TICKET_FRESH_MS) {
          busyTries += 1;
          cancelTimer(connectTimer);
          closeSocket();
          later(() => {
            if (!ended()) connect();
          }, BUSY_RETRY_MS);
        } else startFailed('busy');
        return;
      }
      notStarted(code);
      return;
    }
    finishWith(code === 'backlog' || finishingSlow ? { kind: 'slow' } : { kind: 'unconfirmed' });
  }

  /** The socket went without `saved`. */
  function lost(): void {
    if (ended()) return;
    if (!started) startFailed('no_connection');
    else finishWith(finishingSlow ? { kind: 'slow' } : { kind: 'unconfirmed' });
  }

  function connect(): void {
    const current = ticket;
    if (ended() || current === null) return;
    const ws = opts.connect();
    socket = ws;
    // Each try gets its own time to start.
    cancelTimer(connectTimer);
    connectTimer = later(() => {
      if (socket === ws) startFailed('timeout');
    }, CONNECT_MS);
    ws.binaryType = 'arraybuffer';
    ws.onopen = () => {
      if (socket !== ws || ended()) return;
      ws.send(JSON.stringify({ type: 'start', ...current }));
    };
    ws.onmessage = (message) => {
      if (socket !== ws) return;
      const event = parse(message.data);
      if (event !== null) onEvent(event);
    };
    // A refused handshake (403, 401, 429) closes without opening.
    ws.onclose = () => {
      if (socket !== ws) return;
      socket = null;
      lost();
    };
    ws.onerror = () => undefined;
  }

  /** Gets a ticket (a new one when `renew`) and connects. */
  async function begin(renew: boolean): Promise<void> {
    // A new ticket gets its own busy back-off.
    if (renew) busyTries = 0;
    let got: VoiceTicket | null;
    try {
      got = await opts.ticket(renew);
    } catch {
      startFailed('no_connection');
      return;
    }
    if (ended()) return;
    if (got === null) {
      notStarted('unavailable');
      return;
    }
    ticket = got;
    ticketAt = t.now();
    connect();
  }

  setPhase('starting');
  // A question nobody answers, or a worklet or audio context that never settles, doesn't leave the
  // bar on "Starting the microphone…": the review opens for typing, with MicHelp.
  const opening = new AbortController();
  const micTimer = later(() => {
    // The tracks the browser already gave are stopped, even if the worklet never loaded.
    opening.abort();
    finishWith({ kind: 'microphone', error: new MicrophoneStartTimeout() });
  }, MIC_START_MS);
  void (async () => {
    let opened: Microphone;
    try {
      opened = await opts.openMicrophone(onFrame, opening.signal);
    } catch (error) {
      cancelTimer(micTimer);
      finishWith({ kind: 'microphone', error });
      return;
    }
    cancelTimer(micTimer);
    if (ended()) {
      // Cancelled, or given up, while it was opening: it closes at once.
      void opened.stop();
      return;
    }
    mic = opened;
    setPhase('recording');
    // Recording from now: what's said before the session starts is held, not lost, for as long as
    // the start may take.
    boundTimer = later(() => {
      if (!started) notStarted('timeout');
    }, START_BOUND_MS);
    await begin(false);
  })();

  return {
    done() {
      finish(false);
    },
    typeInstead() {
      if (ended()) return;
      const ws = socket;
      const save = started && ws !== null && ws.readyState === OPEN;
      // Stop, not a dropped socket: what was heard is saved (not "interrupted"). The socket stays
      // until the server closes it, or for the confirm window at most. While finishing, `stop` may
      // already have gone: the socket stays for the same reason.
      if (save && !stopSent) {
        stopSent = true;
        ws.send(JSON.stringify({ type: 'stop' }));
      }
      finishWith({ kind: 'typed' }, save);
      if (save)
        lingerTimer = t.set(() => {
          lingerTimer = null;
          closeSocket();
        }, CONFIRM_MS);
    },
    cancel() {
      // Still opening: let go of what the browser has given so far.
      if (mic === null) opening.abort();
      if (lingerTimer !== null) t.clear(lingerTimer);
      lingerTimer = null;
      if (!ended()) {
        phase = 'ended';
        for (const handle of timers) t.clear(handle);
        timers.clear();
        void stopMic();
        held.length = 0;
        heldBytes = 0;
      }
      closeSocket();
    },
  };
}
